/** Returns the Saturday on or before the given date */
export function getSaturdayWeekStart(date: Date, offsetWeeks = 0): Date {
  const d = new Date(date);
  const day = d.getDay(); // 0=Sun, 6=Sat
  const daysToSubtract = (day + 1) % 7; // Sat→0, Sun→1, Mon→2, ..., Fri→6
  d.setDate(d.getDate() - daysToSubtract + offsetWeeks * 7);
  d.setHours(0, 0, 0, 0);
  return d;
}

/** Returns the 7 dates for a week starting on the given Saturday */
export function getWeekDates(saturdayStart: Date): Date[] {
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(saturdayStart);
    d.setDate(saturdayStart.getDate() + i);
    return d;
  });
}

/**
 * Formats a date as YYYY-MM-DD using LOCAL time components.
 * toISOString() uses UTC, which gives the wrong date in BST (UTC+1) because
 * local midnight is 23:00 the previous day in UTC — causing duplicate date
 * keys and ghost columns when navigating across the DST boundary.
 */
export function toISODate(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** Formats pence as a £ string, e.g. 125 → "£1.25", 80 → "£0.80" */
export function formatPence(pence: number): string {
  return `£${(pence / 100).toFixed(2)}`;
}

/** Formats a week range like "15 Mar – 21 Mar" */
export function formatWeekRange(saturdayStart: Date): string {
  const end = new Date(saturdayStart);
  end.setDate(saturdayStart.getDate() + 6);

  const startDay = saturdayStart.getDate();
  const startMonth = saturdayStart.toLocaleDateString('en-GB', { month: 'short' }) as string;
  const endDay = end.getDate();
  const endMonth = end.toLocaleDateString('en-GB', { month: 'short' }) as string;

  if (startMonth === endMonth) {
    return `${startDay}–${endDay} ${endMonth}`;
  }
  return `${startDay} ${startMonth} – ${endDay} ${endMonth}`;
}

/** Short day label for a date: "Sat", "Sun", etc. */
export function shortDayLabel(date: Date): string {
  return date.toLocaleDateString('en-GB', { weekday: 'short' });
}

/** Weekly earnings target (pence) above which a £2 bonus is paid, per child */
const BONUS_TARGET_PENCE: Record<string, number> = {
  Alex: 600,       // £6
  Alexander: 600,
  Thomas: 400,     // £4
};

const BONUS_PER_MULTIPLE = 200; // £2

/**
 * Returns the bonus pence earned for a given child's approved total.
 * The bonus is £2 for each full multiple of the child's target.
 * e.g. Thomas at £8 approved = 2× £4 target = £4 bonus.
 */
export function calculateBonus(childName: string, approvedPence: number): number {
  const target = BONUS_TARGET_PENCE[childName];
  if (!target || approvedPence < target) return 0;
  return Math.floor(approvedPence / target) * BONUS_PER_MULTIPLE;
}
